import { Dictionary } from '@reduxjs/toolkit';
import { assoc, isNil, map, omit, pick, values, prop } from 'ramda';

import {
  GeoCoordinates,
  UnitType,
  GeoIconType,
  Subscriber,
  IPLink,
  Hybrid,
  NonAESUnit,
  UnitRoute,
  GeoUnitRoute,
  GeoSubscriber,
  GeoIPLink,
  GeoHybrid,
  GeoUnitRouteHop,
  GeoUnitModel,
  GeoFaultsByUnitType,
  GeoFaultByUnitType,
  GeoFaultsByCode,
  GeoFaultByCode,
  GeoLinkLayers,
  GeoAllLinkLayers,
  GeoDependency,
  DependencyTypeColor,
  GeoUnit,
  GeoUnitSearch,
  SubscriberPeers,
  GeoUnitPeers,
} from '~models';
import { feetToKilometers, feetToMiles } from '~utils';

let turf;

import('~utils/turf').then(m => {
  turf = m.turf;
});

type Unit = Subscriber | IPLink | Hybrid | NonAESUnit;

export type GeoUnitPayload = GeoUnitModel & { unitType: UnitType };

export const mapGeoUnitsByType = (units: GeoUnitModel[], isGrouping: boolean) => {
  const data = {
    units: [] as GeoUnitModel[],
    burgIP: [] as GeoUnitModel[],
    fireIP: [] as GeoUnitModel[],
    burgRf: [] as GeoUnitModel[],
    fireRf: [] as GeoUnitModel[],
    unknown: [] as GeoUnitModel[],
    inactive: [] as GeoUnitModel[],
    ipLinks: [] as GeoUnitModel[],
    hybrids: [] as GeoUnitModel[],
    fireHydrants: [] as GeoUnitModel[],
    nonAESUnits: [] as GeoUnitModel[],
    extenders: [] as GeoUnitModel[],
  };

  if (isGrouping) {
    units?.map((unit: GeoUnitModel) => {
      if (unit.unitType === 'IP_LINK') {
        data.ipLinks.push(unit);
        return unit;
      }

      data.units.push(unit);
      return unit;
    });
  } else {
    units?.map((unit: GeoUnitModel) => {
      if ('isActive' in unit && !unit.isActive) {
        data.inactive.push(unit);
        return unit;
      }

      if (unit.iconType === 'unknown') {
        data.unknown.push(unit);
        return unit;
      }

      if (unit.unitType === 'IP_LINK') {
        data.ipLinks.push(unit);
        return unit;
      }

      if (unit.unitType === 'HYBRID') {
        data.hybrids.push(unit);
        return unit;
      }

      if (unit.unitType === 'FIRE_HYDRANT') {
        data.fireHydrants.push(unit);
        return unit;
      }

      if (unit.unitType === 'NON_AES') {
        data.nonAESUnits.push(unit);
        return unit;
      }

      if (unit.hasExtender) {
        data.extenders.push(unit);
        return unit;
      }

      if (unit.unitType === 'SUBSCRIBER' && unit.iconType === 'fire-rf') {
        data.fireRf.push(unit);
        return unit;
      }

      if (unit.unitType === 'SUBSCRIBER' && unit.iconType === 'burglary-ip') {
        data.burgIP.push(unit);
        return unit;
      }
      if (unit.unitType === 'SUBSCRIBER' && unit.iconType === 'burglary-rf') {
        data.burgRf.push(unit);
        return unit;
      }
      if (unit.unitType === 'SUBSCRIBER' && unit.iconType === 'fire-ip') {
        data.fireIP.push(unit);
        return unit;
      }

      data.units.push(unit);
      return unit;
    });
  }

  return data;
};

const getGeoIconType = <T = Unit>(unit: T, unitType: UnitType): GeoIconType => {
  if (((unit as unknown) as GeoUnit)?.hasExtender) {
    return 'extender';
  }

  switch (unitType) {
    case 'SUBSCRIBER': {
      if (((unit as unknown) as Subscriber).type === 'Fire') {
        return ((unit as unknown) as Subscriber).reportingRoute === 'Internet Only' ? 'fire-ip' : 'fire-rf';
      }

      if (((unit as unknown) as Subscriber).type === 'Burg') {
        return ((unit as unknown) as Subscriber).reportingRoute === 'Internet Only' ? 'burglary-ip' : 'burglary-rf';
      }

      return 'unknown';
    }

    case 'HYBRID':
      return 'hybrid';

    case 'IP_LINK':
      return 'ip-link';

    case 'NON_AES':
      return 'non-aes';

    case 'FIRE_HYDRANT':
      return 'fire-hydrant';

    default:
      return 'unknown';
  }
};

const pickCoordinates = (unit: GeoUnitModel) => pick(['latitude', 'longitude'], unit);
export const getCoordinatesByUnitType = (
  unit: { unitType: UnitType; id: number },
  subscribers: Dictionary<GeoSubscriber>,
  ipLinks: Dictionary<GeoIPLink>,
  hybrid: Dictionary<GeoHybrid>
): GeoCoordinates | null => {
  switch (unit.unitType) {
    case 'SUBSCRIBER':
      return pickCoordinates((subscribers[unit.id] as GeoSubscriber) || {});

    case 'IP_LINK':
      return pickCoordinates((ipLinks[unit.id] as GeoIPLink) || {});

    case 'HYBRID':
      return pickCoordinates((hybrid[unit.id] as GeoHybrid) || {});

    default:
      return null;
  }
};

export const mapRoutesWithGeolocation = (
  routes: (GeoUnitRoute | UnitRoute)[],
  subscribers: Dictionary<GeoSubscriber>,
  ipLinks: Dictionary<GeoIPLink>,
  hybrid: Dictionary<GeoHybrid>
): GeoUnitRoute[] =>
  routes.map(route => {
    const firstHopPosition = getCoordinatesByUnitType(
      { unitType: 'SUBSCRIBER', id: route.id },
      subscribers,
      ipLinks,
      hybrid
    );
    const hops = route.hops
      .map(hop => ({
        ...hop,
        ...getCoordinatesByUnitType(hop, subscribers, ipLinks, hybrid),
      }))
      .filter(hop => !isNil(hop.latitude) && !isNil(hop.longitude));

    return {
      ...route,
      ...firstHopPosition,
      unitType: 'SUBSCRIBER',
      hops,
    } as GeoUnitRoute;
  }).filter(hop => !isNil(hop.latitude) && !isNil(hop.longitude));

export const mapGeoUnits = <T = Unit>(units: T[] = [], unitType: UnitType) =>
  map(unit => ({ ...unit, unitType, iconType: getGeoIconType<T>(unit, unitType) }), units);

export const getRouteCoordinates = (route: GeoUnitRoute) => {
  const firstHop = [route.longitude, route.latitude] as number[];
  const hops = (route.hops as GeoUnitRouteHop[]).map(hop => [hop.longitude, hop.latitude]) as number[][];

  return [firstHop, ...hops];
};

export const getPeerCoordinates = (peer, firstLat, firstLong) => {
  const firstPeer = [firstLong, firstLat] as number[][];
  const peerUnits = [peer?.longitude, peer?.latitude] as number[][];

  return [firstPeer, peerUnits];
};

export const getGeoLine = (coords: {longitude: number, latitude: number}[]) => {
  if (coords.length > 1) {
    return turf.helpers.lineString(coords.map(lngLat => [lngLat.longitude, lngLat.latitude]));
  }

  return [];
};

export const mapToFeatureCollection = (coords: number[][]) =>
  turf.helpers.featureCollection(
    coords
      .map((coord, i) =>
        turf.helpers.point(coord, { id: i })
      )
  );

export const mapUnitsToFeatureCollection = (units: GeoCoordinates[]) =>
  turf.helpers.featureCollection(
    units
      .map(unit =>
        turf.helpers.point([unit?.longitude as number, unit?.latitude as number], omit(['longitude', 'latitude'], unit))
      )
  );

export const mapRoutesToFeatureCollection = (routes: GeoUnitRoute[]) => {
  const features = routes
    .map(route => ({ route, coordinates: getRouteCoordinates(route) }))
    .filter(({ coordinates }) => coordinates.length >= 2)
    .map(({ route, coordinates }) => turf.helpers.lineString(coordinates, route));

  return turf.helpers.featureCollection(features);
};

export const mapRulerToFeatureCollection = (coords: Partial<GeoCoordinates>[]) => {
  const points = coords.map(({ longitude, latitude }) =>
    (turf.helpers.point([longitude, latitude])));

  const line = getGeoLine(coords as {longitude: number, latitude: number}[]);

  return turf.helpers.featureCollection([...points, line]);
};

export const mapCircleToFeatureCollection = (units: GeoUnitSearch | null, hydrantRadius: boolean, isMeter: boolean) => {
  const options = { steps: 64, units: isMeter ? 'kilometers' : 'miles' };
  if (!isNil(units)) {
    const radius = isMeter ? feetToKilometers(units?.radius) : feetToMiles(units?.radius);
    if (hydrantRadius && (radius !== 0)) {
      return turf.circle([units.longitude as number, units.latitude as number], radius, options);
    }
  }
  return [];
};

export const mapPeersWithGeolocation = (
  unit: GeoUnitModel | null,
  peers: SubscriberPeers,
  subscribers: Dictionary<GeoSubscriber>,
  ipLinks: Dictionary<GeoIPLink>,
  hybrid: Dictionary<GeoHybrid>
): GeoUnitPeers => {
  const unitCoords = getCoordinatesByUnitType(
    { unitType: unit?.unitType as UnitType, id: unit?.id as number },
    subscribers,
    ipLinks,
    hybrid
  ) as GeoCoordinates;
  const peerCoords = peers?.peers?.map(peer => {
    const peerWithColor = { ...peer, colorProp: peer.quality.label };
    return {
      ...peerWithColor,
      ...getCoordinatesByUnitType({ unitType: peer.unitType, id: peer.unitId }, subscribers, ipLinks, hybrid),
    };
  }).filter(p => !isNil(p.latitude) && !isNil(p.longitude));
  return {
    peers: { peers: peerCoords },
    coords: unitCoords,
  };
};

export const mapPeersToFeatureCollection = (peers: GeoUnitPeers) => {
  const features = peers?.peers?.peers
    ?.map(peer => ({ peer, coordinates: getPeerCoordinates(peer, peers?.coords.latitude, peers?.coords.longitude) }))
    .filter(({ coordinates }) => coordinates.length >= 2)
    .map(({ peer, coordinates }) =>
      turf.helpers.lineString(coordinates, peer));

  return turf.helpers.featureCollection(features);
};

export const mapRoutesWithKeyField = (routes: UnitRoute[]): (UnitRoute & { key: number })[] =>
  routes.map((route, key) => assoc('key', key, route));

export const getGeoFaultsIds = (faults: GeoFaultsByUnitType | GeoFaultsByCode | null): number[] => {
  if (faults) {
    return values(faults)
      .flat()
      .map((fault: GeoFaultByUnitType | GeoFaultByCode) => (fault?.unitId));
  }

  return [];
};

export const getLinkLayersIds = (linkLayers: GeoLinkLayers | null): number[] => {
  if (linkLayers) {
    return Object
      .values(values(linkLayers)
        .flat())
      .flatMap(item => Object.values(item)
        .flat());
  }

  return [];
};

export const setLinkLayers = (linkLayers: GeoAllLinkLayers): GeoLinkLayers => {
  const transform = map(
    map(
      map(prop('unitId'))
    )
  );

  // @ts-ignore
  return transform(linkLayers);
};

export const getDependencyIds = (
  geoDependency: GeoDependency | null
): {id: number, dependency: DependencyTypeColor }[] => {
  if (geoDependency) {
    return Object
      .values(geoDependency)
      .flatMap(dependency => Object.keys(dependency)
        .flatMap(color => dependency[color]
          .map(item =>
            ({ ...item, dependency: color })
          )));
  }

  return [];
};

export const defaultViewport = {
  latitude: 42.55563043952694,
  longitude: -70.97894693424195,
  zoom: 14,
  minZoom: 5,
};

export const getDuplicatedUnits = (units: GeoUnitModel[] | null) => {
  if (units?.length) {
    const coordinates = units?.map(({ latitude, longitude }) => ([longitude, latitude]));
    const coordinateCounts = {};

    coordinates?.forEach(coord => {
      const key = coord.toString();
      coordinateCounts[key] = (coordinateCounts[key] || 0) + 1;
    });

    return Object.keys(coordinateCounts)
      .filter(key => coordinateCounts[key] > 1)
      .map(key => key.split(',').map(Number));
  }

  return [];
};
